import _ from 'lodash';
import { useCallback, useEffect } from 'react';
import { useProjectId } from 'react-scale/corp/views/CorpProjectGroupManager/hooks/useProjectId';
import { PromptDiversityContext } from '@scale/genai-compass-shared/cards/prompt_diversity';
import { Config, ProjectType } from '@scale/shared/types/quality/diversity';

const usePromptDiversityCard = (
  context: PromptDiversityContext,
  updateCardContext: (context: PromptDiversityContext) => void,
  updateCardCanSubmit: (canSubmit: boolean) => void,
) => {
  const projectId: string | undefined = useProjectId();

  const setProjectId = useCallback(
    (projectId: string) => {
      const updatedContext: PromptDiversityContext = { ...context, projectId };
      updateCardContext(updatedContext);
    },
    [context, updateCardContext],
  );

  const setProjectType = useCallback(
    (projectType: ProjectType) => {
      const updatedContext: PromptDiversityContext = { ...context, projectType };
      updateCardContext(updatedContext);
    },
    [context, updateCardContext],
  );

  const setDiversityConfig = useCallback(
    (diversityConfig: Config) => {
      const updatedContext: PromptDiversityContext = { ...context, diversityConfig };
      updateCardContext(updatedContext);
    },
    [context, updateCardContext],
  );

  const setTaskIdColumn = useCallback(
    (taskIdColumn: string) => {
      const updatedContext: PromptDiversityContext = { ...context, taskIdColumn };
      updateCardContext(updatedContext);
    },
    [context, updateCardContext],
  );

  const setPromptColumn = useCallback(
    (promptColumn: string) => {
      const updatedContext: PromptDiversityContext = { ...context, promptColumn };
      updateCardContext(updatedContext);
    },
    [context, updateCardContext],
  );

  useEffect(() => {
    // Force the projectId to be the same as the projectId from the page context, if available
    if (!_.isEmpty(projectId) && context.projectId !== projectId) {
      const updatedContext: PromptDiversityContext = { ...context, projectId };
      updateCardContext(updatedContext);
    }
  }, [context, projectId, updateCardContext]);

  useEffect(() => {
    const canSubmit =
      !_.isEmpty(context.projectId) &&
      !_.isEmpty(context.taskIdColumn) &&
      !_.isEmpty(context.promptColumn) &&
      !_.isEmpty(context.diversityConfig);
    updateCardCanSubmit(canSubmit);
  }, [
    context.projectId,
    context.taskIdColumn,
    context.promptColumn,
    context.diversityConfig,
    updateCardCanSubmit,
  ]);

  return {
    projectId: context.projectId,
    setProjectId,
    projectIdIsEditable: _.isEmpty(projectId),
    projectType: context.projectType,
    setProjectType,
    taskIdColumn: context.taskIdColumn,
    setTaskIdColumn,
    promptColumn: context.promptColumn,
    setPromptColumn,
    diversityConfig: context.diversityConfig as Config,
    setDiversityConfig,
  };
};

export default usePromptDiversityCard;
